/**
 * Overlap enforcement for mask drawing ("disallow_overlap" / one-label-per-voxel).
 *
 * When a mask module has `disallow_overlap` enabled, a newly drawn mask must not
 * claim voxels already owned by another label instance. We enforce this with the
 * "clip" method: on commit, any voxel set in the active mask that is also set in
 * the union of all OTHER mask instances is cleared (existing labels win).
 *
 * Efficiency: callers cache the union across brush strokes (it only changes when
 * the active instance switches, the mask set changes, or mask content is
 * re-hydrated), so per-stroke enforcement is O(voxels) and independent of the
 * number of mask labels.
 *
 * Note on semantics: enforcement operates on the entire active `drawBitmap`, not
 * only the voxels added by the latest stroke. When drawing on a module that has
 * disallow_overlap enabled from the start, masks are clean-by-construction so
 * this is equivalent to clipping only new voxels. But if the flag is enabled on
 * a module whose masks already overlap (or on overlapping seed masks), the first
 * commit on a mask strips its *entire* overlap with other labels — resolving the
 * one-label-per-voxel invariant lazily as each mask is re-committed.
 */

/** Minimal shape of a NiiVue volume needed to build the union. */
interface MaskVolumeLike {
  name?: string
  img?: ArrayLike<number> | null
}

/** Minimal shape of a NiiVue instance needed to build the union. */
interface NiivueVolumesLike {
  volumes: MaskVolumeLike[]
}

/** Prefix used to name mask overlay volumes: `mask:${annotationId}`. */
const MASK_VOLUME_PREFIX = "mask:"

/**
 * Build the union (bitwise OR) of every mask overlay volume EXCEPT the active
 * one, identified by name `mask:${activeAnnotationId}`.
 *
 * Volume `.img` may be aliased to `drawBitmap` for the active instance, which is
 * why exclusion is by name rather than by buffer identity.
 *
 * Returns `null` when there are no other mask volumes (nothing to clip against)
 * or when a length cannot be determined.
 */
export function buildOtherMasksUnion(nv: NiivueVolumesLike, activeAnnotationId: string | null): Uint8Array | null {
  const activeName = activeAnnotationId ? `${MASK_VOLUME_PREFIX}${activeAnnotationId}` : null

  const others: ArrayLike<number>[] = []
  let length = 0
  for (const volume of nv.volumes) {
    const name = volume.name
    if (!(name && name.startsWith(MASK_VOLUME_PREFIX))) continue
    if (name === activeName) continue
    const img = volume.img
    if (!img) continue
    others.push(img)
    if (img.length > length) length = img.length
  }

  if (others.length === 0 || length === 0) return null

  const union = new Uint8Array(length)
  for (const img of others) {
    const n = Math.min(img.length, length)
    for (let i = 0; i < n; i += 1) {
      if (img[i]) union[i] = 1
    }
  }
  return union
}

/**
 * Clip voxels in `drawBitmap` (in place) that overlap `union`. A no-op when
 * `union` is null. Returns the number of voxels that were clipped (useful for
 * tests / debugging).
 */
export function applyOverlapConstraint(drawBitmap: Uint8Array, union: Uint8Array | null): number {
  if (!union) return 0
  const n = Math.min(drawBitmap.length, union.length)
  let clipped = 0
  for (let i = 0; i < n; i += 1) {
    if (drawBitmap[i] && union[i]) {
      drawBitmap[i] = 0
      clipped += 1
    }
  }
  return clipped
}
